import React from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { BLACKOUT_SCOPES, SCOPE_TAG } from "helpers/blackouts"
import styles from "./index.module.css"

/**
 * Scope -> the DSIcon whose glyph matches the design's tag icon: the captain's cap for crew and
 * the sail-over-water `equipmentGear` for equipment, both as Figma 6254:112491 draws them —
 * neither the generic `users` group nor the `box` cube depicts what the pill is naming.
 */
const SCOPE_ICON = {
  [BLACKOUT_SCOPES.BUSINESS]: "blackout",
  [BLACKOUT_SCOPES.LISTING]: "experiences",
  [BLACKOUT_SCOPES.CREW]: "captain",
  [BLACKOUT_SCOPES.EQUIPMENT]: "equipmentGear"
}

/**
 * The scope pill — Business / Listings / Crew / Equipment.
 *
 * Two tones, one per ground the pill sits on:
 *  - `soft`  — the pastel warning pill, keyed to the scope's own colour
 *  - `solid` — the dark pill every blackout row and card carries (Figma 6254:112491): #525455,
 *              the scope's own glyph, and the scope spelled out — "Equipment Blackout"
 *
 * The dark pill stands alone, so it spells the scope out; the warning pill sits in context and
 * keeps the bare noun.
 */
const BlackoutScopeTag = ({ scope, label, tone = "soft", showIcon = true, className = "" }) => {
  const { t } = useTranslation()
  const tag = SCOPE_TAG[scope]

  if (!tag) return null

  const isSolid = tone === "solid"
  // Only the pastel warning pill uses the alert glyph; the other tones name the scope itself.
  const isWarningTone = tone === "soft"

  const iconName = isWarningTone ? "AlertTriangle" : SCOPE_ICON[scope]

  const fallbackLabel = isSolid
    ? t(tag.solidLabelKey, { defaultValue: tag.solidLabelDefault })
    : t(tag.labelKey, { defaultValue: tag.labelDefault })

  return (
    <span className={`${styles.tag} ${isWarningTone ? styles[tag.variant] : styles[tone]} ${className}`}>
      {showIcon && (
        <Icon
          type={isWarningTone ? "feather" : "ds"}
          name={iconName}
          width={isWarningTone ? 12 : 16}
          height={isWarningTone ? 12 : 16}
          color="currentColor"
        />
      )}
      <span className={styles.label}>{label || fallbackLabel}</span>
    </span>
  )
}

BlackoutScopeTag.propTypes = {
  scope: PropTypes.string.isRequired,
  /** Overrides the scope's default copy, e.g. "All Experiences Blackout". */
  label: PropTypes.string,
  tone: PropTypes.oneOf(["soft", "solid"]),
  showIcon: PropTypes.bool,
  className: PropTypes.string
}

export default BlackoutScopeTag
